<template>
    <v-menu
        v-model="isOpen"
        :close-on-content-click="false"
        min-width="312"
        nudge-left="50%"
        top
        offset-y
        transition="slide-y-transition"
        content-class="v-application"
    >
        <template v-slot:activator="{ on, attrs }">
            <span
                v-bind="attrs"
                v-on="on"
                :class="{'ml-3': isPublicationItem}"
            >
                <v-timeline-item
                    :icon="!isPublicationItem ? `mdi-numeric-${index}` : 'mdi-alpha-p'"
                    :color="getChunkStatusColor"
                    :class="[`${getChunkStatusColor}-shadow`, `pb-0`, {'numeric-chunk': !isPublicationItem}]"
                    small
                    fill-dot
                ></v-timeline-item>
            </span>
        </template>

        <v-card v-if="!isPublicationItem" :loading="isLoading">
            <v-card-title class="justify-space-between">
                <div>
                    <span class="h3">Chunk {{ index }} - </span>
                    <span class="ml-2">{{ getChunkStatusName }}</span>
                    <v-icon
                        v-if="status === 'skipped_empty'"
                        color="error"
                        class="ml-2"
                    >mdi-debug-step-over</v-icon>
                    <v-icon
                        v-else-if="status === 'sent'"
                        color="success"
                        class="ml-2"
                    >mdi-check-circle-outline</v-icon>
                    <v-icon
                        v-else-if="status === 'scheduled'"
                        color="warning"
                        class="ml-2"
                    >mdi-calendar-clock</v-icon>
                </div>
                <v-btn
                    @click="sendNow"
                    v-if="status === 'scheduled'"
                    color="success"
                    class="ml-3"
                    small
                >
                    Senden
                    <v-icon class="ml-2" size="18">mdi-check-circle-outline</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text>
                <p class="h5">
                    Zeit: {{ formatDate }}
                </p>
                <v-row>
                    <v-col :cols="status === 'scheduled' ? 'auto' : '12'">
                        <v-list v-if="pharmacists.length" max-height="330" class="overflow-y-auto">
                            <v-subheader>Freiberufler</v-subheader>
                            <template v-for="(pharmacist, idx) in pharmacists">
                                <v-list-item dense>
                                    <v-list-item-content class="p-0">
                                        <a :href="`/pharmacists/${pharmacist.id}`" target="_blank">
                                            {{ pharmacist.name }}
                                        </a>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider v-if="idx < pharmacists.length - 1" />
                            </template>
                        </v-list>
                    </v-col>
                    <v-col v-if="status === 'scheduled'" cols="auto">
                        <vc-date-picker
                            v-model="scheduledTime"
                            :minute-increment="15"
                            mode="datetime"
                            locale="de"
                            is24hr
                        />
                        <v-btn
                            @click="schedule"
                            :disabled="!scheduledTime"
                            color="primary"
                            class="mt-2"
                            small
                            depressed
                            block
                        >
                            Chunkzeit ändern
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-divider></v-divider>
        </v-card>
        <v-card v-else>
            <v-card-title>
                <span class="h3">Publikation - </span>
                <span class="ml-2">{{ getChunkStatusName }}</span>
                <v-icon
                    v-if="status === 'sent'"
                    color="success"
                    class="ml-2"
                >mdi-check-circle-outline</v-icon>
            </v-card-title>
            <v-card-text>
                <p class="h5">
                    Zeit: {{ formatDate }}
                </p>
            </v-card-text>
        </v-card>
    </v-menu>
</template>

<script>
import axios from "axios";
import { mapMutations } from 'vuex';

export default {
    props: {
        index: Number,
        status: String,
        time: String,
        appointmentId: String,
        isPublicationItem: Boolean,
    },
    data() {
        return {
            pharmacists: [],
            isOpen: false,
            isLoading: false,
            scheduledTime: null
        }
    },
    methods: {
        async getChunkPharmacists() {
            this.isLoading = true;
            try {
                const response = await axios(`/api/appointments/${this.appointmentId}/chunks/${this.index}`);
                this.pharmacists = response.data;
            } catch {
                this.alertError()
            } finally {
                this.isLoading = false;
            }
        },
        async sendNow() {
            try {
                const response = await axios.post(`/api/appointments/${this.appointmentId}/chunks/${this.index}/send_now`);
                if (!response.status === 200) throw new Error();
                this.alertSuccess('chunk erfolgreich gesendet');
                this.$emit('fetch-appointments');
                this.isOpen = false;
            } catch {
                this.alertError()
            }
        },
        async schedule() {
            try {
                const response = await axios.post(`/api/appointments/${this.appointmentId}/chunks/${this.index}/set_time`, {}, {
                    params: {
                        send_time: this.scheduledISOTime
                    }
                });
                if (!response.status === 200) throw new Error();
                this.alertSuccess('chunk erfolgreich geplant');
                this.$emit('fetch-appointments');
                this.isOpen = false;
            } catch {
                this.alertError()
            }
        },
        ...mapMutations({
            alertError: 'alert/error',
            alertSuccess: 'alert/success',
        })
    },
    computed: {
        getChunkStatusColor() {
            const chunkColors = {
                skipped_empty: 'red',
                scheduled: 'orange',
                next: 'yellow',
                sent: 'green'
            }
            return chunkColors[this.status] || 'grey'
        },
        getChunkStatusName() {
            const chunkColors = {
                skipped_empty: 'Übersprungen',
                scheduled: 'Geplant',
                next: 'Als nächstes',
                sent: 'Gesendet'
            }
            return chunkColors[this.status] || 'Ausstehend'
        },
        scheduledISOTime() {
            return this.scheduledTime ? new Date(this.scheduledTime).toISOString() : null
        },
        formatDate() {
            if (!this.time) {
                return '-';
            }
            let date = String(this.time);
            if (!/[zZ]|[+-]\d\d:?\d\d$/.test(date)) date += 'Z'; // for date strings without Z
            return moment(date).format('DD-MM-YYYY, HH:mm');
        },
    },
    watch: {
        isOpen(isOpen) {
            if (!this.isPublicationItem && isOpen) this.getChunkPharmacists();
        }
    },
}
</script>
<style></style>
